import React, { useState , useRef, useCallback, useEffect} from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useUsuario } from '../../context/usuarioContext';
import { yupResolver } from '@hookform/resolvers/yup';
import './recepcion-inicial.css';

// Componente UI
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { classNames } from 'primereact/utils';
import { Button } from 'primereact/button';
import { AutoComplete } from 'primereact/autocomplete';
import { Tooltip } from 'primereact/tooltip';

// Componentes
import VehiculoDanosCanvas from '../vehiculo-canvas/danos-canvas';
import EvaluacionDanosLista from './evaluacion-danos';

// Datos
import { buscarPatentes, obtenerVehiculoById } from '../../services/vehiculosService';
import clienteService from '../../services/revisionClienteService';
import { useCatalogosMecanica } from '../../hooks/cat-recepcion-tm';
import { buildPayloadPaso1 } from '../../utils/datos-estructurados/revisionBuilder';

// Validación
import { recepcionSchema } from '../../validation/recepcion-inicial';

// Assets
import sedanImg     from '../../assets/siluetas/sedan.svg';
import stariaImg    from '../../assets/siluetas/staria.svg';
import sprinterImg  from '../../assets/siluetas/sprinter.svg';
import camionetaImg from '../../assets/siluetas/camioneta.svg';

const SILUETAS = [
  { tipo: 'sedan',     label: 'Sedán',     src: sedanImg },
  { tipo: 'van',       label: 'VAN',       src: stariaImg },
  { tipo: 'sprinter',  label: 'Sprinter',  src: sprinterImg },
  { tipo: 'camioneta', label: 'Camioneta', src: camionetaImg },
];

const OPCIONES_COMBUSTIBLE = [
  { label: 'Bencina', value: 'Bencina' },
  { label: 'Diésel', value: 'Diésel' },
  { label: 'Eléctrico', value: 'Eléctrico' },
  { label: 'Híbrido', value: 'Híbrido' },
];

const NIVELES_COMBUSTIBLE = [
  { label: 'Vacío / 0%', value: 0 },
  { label: '1/4 (25%)', value: 25 },
  { label: '1/2 (50%)', value: 50 },
  { label: '3/4 (75%)', value: 75 },
  { label: 'Lleno (100%)', value: 100 },
];

function CampoError({ children }) {
  if (!children) return null;
  return <small className="p-error d-block mt-1">{children}</small>;
}

/**
 * Props:
 *  - onRegistrar: (payload) => Promise<void>   // POST /api/informes
 */
function RecepcionInicial({ onRegistrar, valoresIniciales, soloLectura }) {
  const { usuario } = useUsuario();
  const { centros, servicios, mecanicos, partesVehiculo } = useCatalogosMecanica();
  const [enviando, setEnviando] = useState(false);
  const [patentes, setPatentes] = useState([]);
  const [sugerenciasCliente, setSugerenciasCliente] = useState([]);
  const canvasRef = useRef(null);
  const debounceRef = useRef(null);
  const [trazosDanos, setTrazosDanos] = useState(
    () => valoresIniciales?.diagrama?.trazos ?? []
  );

  // Quien recepciona: si el registro ya existe, se respeta SIEMPRE el trabajador
  // que quedó guardado en él (el que lo creó). Nunca se reemplaza por quien edita.
  const tecnicoRecepcionaDefault = valoresIniciales?.trabajadores?.find(
    (t) => t.tipo_trabajador === 'tecnico') ?? null;
  const idTecnicoDefault = tecnicoRecepcionaDefault?.id_trabajador ?? null;
  const nombreTecnicoDefault = tecnicoRecepcionaDefault?.nombre ?? '';

  const idServiciosDefault = (valoresIniciales?.servicios ?? []).map((s) => s.id_servicio);

  const idMecanicosDefault = (valoresIniciales?.trabajadores ?? [])
    .filter((t) => t.tipo_trabajador === 'mecanico')
    .map((t) => t.id_trabajador);

  const evaluacionDanosDefault = (valoresIniciales?.evaluacion ?? []).reduce((acc, item) => {
    acc[item.id_parte] = item.estado;
    return acc;
  }, {});

  const {
    control, handleSubmit, watch, setValue, formState: { errors },
  } = useForm({
    resolver: yupResolver(recepcionSchema),
    mode: 'onTouched',
    defaultValues: {
      // recepcion
      id_centrocosto: valoresIniciales?.id_centrocosto ?? null,
      id_tecnico: idTecnicoDefault ?? usuario?.id_trabajador ?? usuario?.id ?? null,
      id_tecnico_nombre: nombreTecnicoDefault || usuario?.nombre_apellido || '',
      id_servicios: idServiciosDefault,
      id_mecanicos: idMecanicosDefault,
      fechaIngreso: valoresIniciales?.fecha_ingreso ? new Date(valoresIniciales.fecha_ingreso) : null,
      fechaSalida: valoresIniciales?.fecha_salida ? new Date(valoresIniciales.fecha_salida) : null,
      km_ingreso: valoresIniciales?.km_ingreso ?? null,
      km_prox_mantencion: valoresIniciales?.km_prox_mantencion ?? null,
      sintomas: valoresIniciales?.motivo_ingreso ?? '',
      nivel_combustible: valoresIniciales?.nivel_combustible ?? null,
      // vehiculo
      id_vehiculo: valoresIniciales?.vehiculo?.id_vehiculo ?? null,
      patente: valoresIniciales?.vehiculo?.patente ?? '',
      marca: valoresIniciales?.vehiculo?.marca ?? '',
      modelo: valoresIniciales?.vehiculo?.modelo ?? '',
      anio: valoresIniciales?.vehiculo?.anio || null,
      nmotor: valoresIniciales?.vehiculo?.nmotor ?? '',
      nserie: valoresIniciales?.vehiculo?.nserie ?? '',
      kilometraje_act: valoresIniciales?.vehiculo?.kilometraje_act ?? null,
      kilometraje_prox: valoresIniciales?.vehiculo?.kilometraje_prox ?? null,
      tipo_combustible: valoresIniciales?.vehiculo?.tipo_combustible ?? null,
      // cliente
      id_cliente: valoresIniciales?.cliente?.id_cliente ?? null,
      clienteNombre: valoresIniciales?.cliente?.nombre ?? '',
      clienteRut: valoresIniciales?.cliente?.rut ?? '',
      clienteCelular: valoresIniciales?.cliente?.celular ?? '',
      clienteCorreo: valoresIniciales?.cliente?.correo ?? '',
      // evaluacion de partes
      evaluacionDanos: evaluacionDanosDefault,
      // diagrama imagen
      imagenVehiculoTipo: valoresIniciales?.diagrama?.tipo_silueta ?? '',
    },
  });

  useEffect(() => {
    if (valoresIniciales?.diagrama?.trazos?.length) {
      setTrazosDanos(valoresIniciales.diagrama.trazos);
    }
  }, [valoresIniciales]);

  useEffect(() => {
    // Solo se autocompleta en creación. Si el registro ya trae un tecnicoRecepcionaDefault,
    // nunca se toca: eso es lo que garantiza que quien recepciona quede fijo en A aunque B edite.
    if (!tecnicoRecepcionaDefault && usuario) {
      setValue('id_tecnico', usuario.id_trabajador ?? usuario.id, { shouldValidate: true });
      setValue('id_tecnico_nombre', usuario.nombre_apellido ?? '');
    }
  }, [usuario, tecnicoRecepcionaDefault, setValue]);


  const siluetaSeleccionada = watch('imagenVehiculoTipo');
  const siluetaActiva = SILUETAS.find((s) => s.tipo === siluetaSeleccionada);

  const buscarPatente = async (eventoAutocomplete) => {
    const patenteBuscada = eventoAutocomplete?.query?.toUpperCase()?.trim() ?? '';

    if (patenteBuscada.length < 2) {
      setPatentes([]);
      return;
    }

    try {
      const vehiculos = await buscarPatentes(patenteBuscada);
      setPatentes(vehiculos || []);
    } catch (e) { 
      setPatentes([]); 
    }
  };
  
  const seleccionarVehiculo = async (e) => {
    try {
      const id_vehiculo = e.id_vehiculo;
      const v = await obtenerVehiculoById(id_vehiculo);
      if (!v) return;

      setValue("patente",         v.patente,           { shouldDirty: true });
      setValue("id_vehiculo",     v.id_vehiculo || "", { shouldDirty: true });
      setValue("marca",           v.marca       || "", { shouldDirty: true });
      setValue("modelo",          v.modelo      || "", { shouldDirty: true });
      setValue("anio",            v.anio        ?? null, { shouldDirty: true });
      setValue("nserie",          v.nserie      || '', { shouldDirty: true });
      setValue("nmotor",          v.nmotor      || '', { shouldDirty: true });
      setValue("km_ingreso",      v.kmActual    ??  null, { shouldDirty: true });
    
    } catch (e) {
      console.error(e);
    }
  }

  const buscarClientePorRut = useCallback((eventoAutocomplete) => {
    const rutIngresado = eventoAutocomplete.query;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (!rutIngresado || rutIngresado.length < 3) {
        setSugerenciasCliente([]);
        return;
      }
      try {
        const resultados = await clienteService.buscarPorRut(rutIngresado);
        setSugerenciasCliente(resultados);
      } catch (err) {
        setSugerenciasCliente([]);
      }
    }, 350);
  }, []);

  const seleccionarCliente = (e) => {
    const cliente = e.value;

    setValue('id_cliente',     cliente.id_cliente, { shouldValidate: true });
    setValue('clienteRut',     cliente.rut,        { shouldValidate: true });
    setValue('clienteNombre',  cliente.nombre,     { shouldValidate: true });
    setValue('clienteCelular', cliente.celular,    { shouldValidate: true });
    setValue('clienteCorreo',  cliente.correo,     { shouldValidate: true });
  };

  const onSubmit = async (data) => {
    setEnviando(true);
    if (!onRegistrar) return;

    try {
      const imagenFinal = canvasRef.current?.exportarComoImagen?.() ?? null;
      const payload = buildPayloadPaso1(data, trazosDanos, imagenFinal, valoresIniciales);
      await onRegistrar(payload);
    } finally {
      setEnviando(false);
    }
  };

  const onError = (errors) => {
    const primerCampo = Object.keys(errors)[0];

    const elemento = document.querySelector(
      `[name="${primerCampo}"]`
    );

    elemento?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  return (
    <section className="tmc-form mt-3 mb-6">
      <form 
        className="rev-paso1 container-form "
        onSubmit={handleSubmit(onSubmit, onError)}
      > 

        {/* ---------------- Centro / Servicio / Técnico / Mecánico ---------------- */}
        <div className='card-nc'>
          <h5 className="mb-3">I. Datos de recepción</h5>
          {/* Centro */} 
          <div className="form-comprobante-row">
            <div className="mb-2 form-comprobante-field">
              <label className="form-label fw-bold">Centro de costo<span className="required">*</span></label>
              <Controller
                name="id_centrocosto"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    {...field}
                    options={centros}
                    optionLabel="nombre"
                    optionValue="id_centrocosto"
                    placeholder="Selecciona centro de costo"
                    className={classNames(' w-100', { 'p-invalid': errors.id_centrocosto })}
                    onChange={(e) => field.onChange(e.value)}
                    emptyMessage='No hay opciones disponibles'
                  />
                )}
              />
              <CampoError>{errors.id_centrocosto?.message}</CampoError>
            </div>
            {/* Recepciona */}
            <div className="mb-2 form-comprobante-field">
              <label className="form-label">Recepciona</label>
              <Controller
                  name="id_tecnico_nombre"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      {...field}
                      value={field.value ?? ''}
                      disabled
                      className="w-100"
                    />
                  )}
              />
              <CampoError>{errors.id_tecnico?.message}</CampoError>
            </div>
          </div>
          {/* Servicio */}
          <div className="form-comprobante-row">
            <div className="mb-2 form-comprobante-field">
              <label className="form-label fw-bold">Servicios <span className="required">*</span></label>
              <Controller
                name="id_servicios"
                control={control}
                defaultValue={[]}
                render={({ field }) => (
                  <MultiSelect
                    value={field.value ?? []}
                    options={servicios}
                    optionLabel="nombre"
                    optionValue="id_servicio"
                    placeholder='Selecciona servicio(s)'
                    className={classNames(' w-100', { 'p-invalid': errors.id_servicios })}
                    onChange={(e) => {field.onChange(e.value)}}
                    onBlur={field.onBlur}
                    display="chip"
                    filter
                    emptyMessage="No hay opciones disponibles"
                  />
                )}
              /><CampoError>{errors.id_servicios?.message}</CampoError>
            </div>
          </div>
          
          {/* Mecanico */}
          <div className="form-comprobante-row">
            <div className="mb-2 form-comprobante-field">
              <label className="form-label fw-bold">Mecánico(s) asignado(s) <span className="required">*</span></label>
              <Controller
                name="id_mecanicos"
                control={control}
                defaultValue={[]}
                render={({ field }) => (
                  <MultiSelect
                    value={field.value ?? []}
                    options={mecanicos}
                    optionLabel="nombre"
                    optionValue="id_trabajador"
                    placeholder="Selecciona mecánico(s)"
                    className={classNames(' w-100', { 'p-invalid': errors.id_mecanicos })}
                    onChange={(e) => field.onChange(e.value)}
                    onBlur={field.onBlur}
                    display="chip"
                    filter
                    emptyMessage='No hay opciones disponibles'
                  />
                )}
              />
              <CampoError>{errors.id_mecanicos?.message}</CampoError>
            </div>
          </div>
          <div className="form-comprobante-row">
            <div className="mb-2 form-comprobante-field">
              <label className="form-label fw-bold">Fecha recepción <span className="required">*</span></label>
              <Controller
                name="fechaIngreso"
                control={control}
                render={({ field }) => (
                  <Calendar
                    {...field}
                    value={field.value ?? null}
                    dateFormat="dd/mm/yy"
                    className={classNames(' w-100', { 'p-invalid': errors.fechaIngreso })}
                    onChange={(e) => field.onChange(e.value)}
                  />
                )}
              />
              <CampoError>{errors.fechaIngreso?.message}</CampoError>
            </div>
            <div className="mb-2 form-comprobante-field">
              <label className="form-label">Fecha de salida</label>
              <Controller
                name="fechaSalida"
                control={control}
                render={({ field }) => (
                  <Calendar
                    {...field}
                    value={field.value ?? null}
                    dateFormat="dd/mm/yy"
                    className=" w-100"
                    onChange={(e) => field.onChange(e.value)}
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* ---------------- Vehículo ---------------- */}
        <div className='card-nc'>
          <h5 className="mb-3">II. Datos del vehículo</h5>
          <div className="form-comprobante-row">
            <div className="mb-2 form-comprobante-field">
              <label className="form-label fw-bold">
                Patente <span className="required">*</span>
              </label> <small className="text-muted">(Buscador)</small>
              <Controller
                name="patente"
                control={control}
                className= {'w-100'}
                render={({ field, fieldState }) => (
                  <div>
                    <AutoComplete
                      value={field.value ?? ""}
                      suggestions={patentes}
                      completeMethod={buscarPatente}
                      field="patente"
                      itemTemplate={(item) => item.patente}
                      placeholder="BUSCAR PATENTE..."
                      onChange={(e) => {
                        const valor = typeof e.value === 'string'
                          ? e.value.toUpperCase()
                          : e.value?.patente ?? '';
                        field.onChange(valor);
                      }}
                      onSelect={(e) => { seleccionarVehiculo(e.value)}}
                      maxLength={10}
                      className={classNames('w-100', { 'p-invalid': errors.patente })}
                      inputClassName="input-buscar w-100"
                    />
                    <CampoError>{errors.patente?.message}</CampoError>
                  </div>
                )}
              />
            </div> 
            <div className="mb-2 form-comprobante-field">
              <label className="form-label fw-bold">Año <span className="required">*</span></label>
              <Controller
                name="anio"
                control={control}
                render={({ field }) => (
                  <InputNumber
                    value={field.value ?? null}
                    onValueChange={(e) => field.onChange(e.value)}
                    useGrouping={false}
                    max={2100}
                    className={classNames('w-100', { 'p-invalid': errors.anio })}
                    inputClassName="w-100"
                  />
                )}
              />
              <CampoError>{errors.anio?.message}</CampoError>
            </div>
          </div>
          <div className="form-comprobante-row">
            <div className="mb-2 form-comprobante-field">
              <label className="form-label fw-bold">Marca <span className="required">*</span></label>
              <Controller
                name="marca"
                control={control}
                render={({ field }) => (
                  <InputText {...field} 
                    value={field.value.toUpperCase() ?? ""} 
                    maxLength={25}
                    className={classNames(' w-100', { 'p-invalid': errors.marca })} 
                  />
                )}
              />
              <CampoError>{errors.marca?.message}</CampoError>
            </div>
            <div className="mb-2 form-comprobante-field">
              <label className="form-label fw-bold">Modelo <span className="required">*</span></label>
              <Controller
                name="modelo"
                control={control}
                render={({ field }) => (
                  <InputText {...field} 
                    value={field.value.toUpperCase() ?? ""} 
                    maxLength={50}
                    className={classNames(' w-100', { 'p-invalid': errors.modelo })} 
                  />
                )}
              />
              <CampoError>{errors.modelo?.message}</CampoError>
            </div>
          </div>
          <div className="form-comprobante-row">
            <div className="mb-2 form-comprobante-field">
              <label className="form-label">N° Motor</label>
              <Controller
                name="nmotor"
                control={control}
                render={({ field }) => 
                  <InputText 
                    {...field} 
                    value={field.value.toUpperCase() ?? ""} 
                    maxLength={30}
                    className=" w-100" 
                  />
                }
              />
            </div>
            <div className="mb-2 form-comprobante-field">
              <label className="form-label">N° Serie / Chasis</label>
              <Controller
                name="nserie"
                control={control}
                render={({ field }) => 
                  <InputText 
                    {...field} 
                    value={field.value.toUpperCase() ?? ""} 
                    maxLength={30}
                    className=" w-100" 
                  />
                }
              />
            </div>
          </div>
          <div className="form-comprobante-row form-comprobante-row--km">
            <div className="mb-2 form-comprobante-field">
              <label className="form-label fw-bold">
                Kilometraje ingreso <span className="required">*</span>
                </label>
              <Controller
                name="km_ingreso"
                control={control}
                render={({ field }) => (
                  <InputNumber
                    value={field.value ?? null}
                    onValueChange={(e) => field.onChange(e.value)}
                    suffix=" km"
                    maxLength={10}
                    className={classNames(' w-100', { 'p-invalid': errors.km_ingreso})}
                    min={0} max={999999}
                  />
                )}
              /><CampoError>{errors.km_ingreso?.message}</CampoError>
            </div>
            <div className="mb-2 form-comprobante-field">
              <label className="form-label fw-bold">
                Próxima mantención en
                <i
                  className="pi pi-info-circle ms-1 info-tooltip"
                  data-pr-tooltip="Ingresa cuántos km faltan (ej: 10.000), no el km final. Se calculará automáticamente."
                  data-pr-position="top"
                />
                <Tooltip target=".info-tooltip" />
              </label>
              <Controller
                name="km_prox_mantencion"
                control={control}
                render={({ field }) => (
                  <InputNumber
                    value={field.value ?? null}
                    onValueChange={(e) => field.onChange(e.value)}
                    suffix=" km"
                    maxLength={10}
                    className=" w-100"
                    min={0} max={999999}
                  />
                )}
              />
            </div>
          </div>
          <div className="form-comprobante-row">
            <div className="mb-2 form-comprobante-field">
              <label className="form-label">Tipo de combustible</label>
              <Controller
                name="tipo_combustible"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    {...field}
                    value={field.value ?? null}
                    options={OPCIONES_COMBUSTIBLE}
                    placeholder="Selecciona combustible"
                    className=" w-100"
                    onChange={(e) => field.onChange(e.value)}
                  />
                )}
              />
            </div>
            <div className="mb-2 form-comprobante-field">
              <label className="form-label">
                {watch('combustible') === 'electrico'
                  ? 'Nivel de energía'
                  : 'Nivel de combustible'}
              </label>
              <Controller
                name="nivel_combustible"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    {...field}
                    value={field.value ?? null}
                    options={NIVELES_COMBUSTIBLE}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Selecciona nivel"
                    className=" w-100"
                    onChange={(e) => field.onChange(e.value)}
                  />
                )}
              />
            </div>
          </div>
          <div className="form-comprobante-row">
            <div className="mb-2 form-comprobante-field">
              <label className="form-label fw-bold">Síntomas / Motivo de ingreso</label>
              <Controller
                name="sintomas"
                control={control}
                render={({ field }) => (
                  <>
                    <InputTextarea 
                      {...field}
                      value={field.value ?? ""}
                      rows={4}
                      autoResize
                      maxLength={500}
                      className=" w-100"
                      style={{ minHeight: '100px' }}
                    />
                    <small className="d-block text-end text-muted">
                      {(field.value ?? "").length}/500
                    </small>
                  </>
                )}
              />
            </div>
          </div>
        </div>

        {/* ---------------- Cliente ---------------- */}
        <div className='card-nc'>
          <h5 className="mb-3">III. Datos del cliente</h5>
          <div className="form-comprobante-row">
            <div className="mb-2 form-comprobante-field">
              <label className="form-label fw-bold">RUT <span className="required">* </span>
              </label> <small className="text-muted">(Buscador)</small>
              <Controller
                name="clienteRut"
                control={control}
                render={({ field }) => (
                  <AutoComplete
                    value={field.value ?? ""}
                    suggestions={sugerenciasCliente}
                    completeMethod={buscarClientePorRut}
                    field="rut" // qué mostrar en el input cuando eliges una opción
                    itemTemplate={(cliente) => (
                      <div><strong>{cliente.rut}</strong> | {cliente.nombre}</div>
                    )}
                    placeholder="BUSCAR RUT..."
                    onChange={(e) => field.onChange(typeof e.value === 'string' ? e.value : e.value.rut)}
                    onSelect={seleccionarCliente}
                    maxLength={10}
                    className={classNames('w-100', { 'p-invalid': errors.clienteRut })}
                    inputClassName="input-buscar w-100"
                  />
                )}
              />
              <CampoError>{errors.clienteRut?.message}</CampoError>
            </div>
            <div className="mb-2 form-comprobante-field">
              <label className="form-label fw-bold">Nombre <span className="required">*</span></label>
              <Controller
                name="clienteNombre"
                control={control}
                render={({ field }) => (
                  <InputText {...field} 
                    value={field.value ?? ""} 
                    maxLength={50}
                    className={classNames('w-100', { 'p-invalid': errors.clienteNombre })}
                  />
                )}
              />
              <CampoError>{errors.clienteNombre?.message}</CampoError>
            </div>
          </div>
            <div className="form-comprobante-row">
            <div className="mb-2 form-comprobante-field">
              <label className="form-label fw-bold">Celular <span className="required">*</span></label>
              <Controller
                name="clienteCelular"
                control={control}
                render={({ field }) => (                  
                  <InputNumber
                    value={field.value ?? null}
                    onValueChange={(e) => field.onChange(e.value)}
                    useGrouping={false}
                    maxLength={9}
                    className={classNames('w-100', { 'p-invalid': errors.celular })}
                    inputClassName="w-100"
                  />
                )}
              />
              <CampoError>{errors.clienteCelular?.message}</CampoError>
            </div>
            <div className="mb-2 form-comprobante-field">
              <label className="form-label">Correo</label>
              <Controller
                name="clienteCorreo"
                control={control}
                render={({ field }) => (
                  <InputText 
                    {...field} 
                    value={field.value ?? ""} 
                    className={classNames('w-100', { 'p-invalid': errors.clienteCorreo })} 
                    placeholder='ejemplo@correo.com'
                  />
                )}
              />
              <CampoError>{errors.clienteCorreo?.message}</CampoError>
            </div>
          </div>
        </div>

        {/* ---------------- Revisión Partes ---------------- */}
        <div className='card-nc'>
          <h5 className="mb-3">IV. Evaluación de partes del vehículo</h5>
          <Controller
            name="evaluacionDanos"
            control={control}
            render={({ field }) => (
              <EvaluacionDanosLista
                partes={partesVehiculo}
                value={field.value ?? {}}
                onChange={field.onChange}
              />
            )}
          />
        </div>

        {/* ---------------- Diagrama daños ---------------- */}
        <div className='card-nc'>
          <h5 className="mb-3">V. Diagrama de daños</h5>

          <div className="d-flex flex-wrap gap-3 mb-3">
            {SILUETAS.map((s) => (
              <div
                key={s.tipo}
                className={`border rounded p-2 text-center silueta-card ${
                  siluetaSeleccionada === s.tipo ? 'silueta-card--activa' : ''
                }`}
                style={{ cursor: 'pointer', width: 130 }}
              >
                <Controller
                  name="imagenVehiculoTipo"
                  control={control}
                  render={({ field }) => (
                    <div onClick={() => field.onChange(s.tipo)}>
                      <img src={s.src} alt={s.label} style={{ width: '100%' }} />
                      <div className="small mt-1">{s.label}</div>
                    </div>
                  )}
                />
              </div>
            ))}
          </div>

          {siluetaActiva && (
            <VehiculoDanosCanvas
              ref={canvasRef}
              siluetaUrl={siluetaActiva.src}
              trazosIniciales={trazosDanos}
              onChange={setTrazosDanos}
            />
          )}
        </div>

        <div className='contenedor-botones'>
          <Button 
            type="submit" 
            icon="pi pi-check-circle"
            label={enviando ? 'Registrando...' : 'Registrar Recepción'}
            disabled={enviando}
            className="btn-lleno-verde"
          />
        </div>
      </form>
    </section>
  );
}

export default RecepcionInicial;